import {
  BUMP_MINIMAL_WORKED_HOURS,
  BumpResourceSheetType,
  JobPhaseBumpApprovalStatus,
  RepairBumperPaymentTypeEnum,
  RepairFormulaInterface,
  RepairTypeEnum,
} from '@vatos-pas/common';
import { DateTime } from 'luxon';

const findRepairFormula = (
  repairFormulas: RepairFormulaInterface[] | undefined,
  repairId: string,
  paymentType: RepairBumperPaymentTypeEnum,
) => {
  return repairFormulas?.find(item => {
    return (
      item.repairId === repairId && item.repairBumperPaymentType === paymentType
    );
  });
};

const getContractorFilter = (
  repairFormulas: RepairFormulaInterface[] | undefined,
  repairId: string,
  regionId: string,
  bumpResourceType: string,
) => {
  const commonFilter = {
    active: true,
    suspended: false,
    archived: false,
    bumperAvailable: true,
    'contractorRegions.regionId': regionId,
    $join: [{ field: 'contractorRegions' }],
  };

  const isRepairPayment =
    bumpResourceType === BumpResourceSheetType.REPAIR_PAYMENT;

  if (!isRepairPayment) {
    return commonFilter;
  }

  const perBoard = findRepairFormula(
    repairFormulas,
    repairId,
    RepairBumperPaymentTypeEnum.PerBoard,
  );
  const perHour = findRepairFormula(
    repairFormulas,
    repairId,
    RepairBumperPaymentTypeEnum.PerHour,
  );

  const hasPerBoard = perBoard !== undefined;
  const hasPerHour = perHour !== undefined;

  return {
    ...commonFilter,
    ...(isRepairPayment &&
      !hasPerBoard &&
      hasPerHour && {
        'bumperPaymentType||$eq': RepairBumperPaymentTypeEnum.PerHour,
      }),
    ...(isRepairPayment &&
      hasPerBoard &&
      !hasPerHour && {
        'bumperPaymentType||$eq': RepairBumperPaymentTypeEnum.PerBoard,
      }),
  };
};

/**
 * Round hours according to backend logic:
 * - 0h01-0h14 → 0.5 (Rule 1)
 * - 0-14 min (x≥1h) → floor
 * - 15-44 min → +0.5
 * - 45-59 min → +1
 */
const roundHours = (hours: number): number => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes >= 0 && minutes <= 14) {
    // Rule 1: if total time is between 0h01 and 0h14, round to 0.5h
    if (wholeHours === 0 && minutes > 0) {
      return 0.5;
    }
    return wholeHours;
  } else if (minutes >= 15 && minutes <= 44) {
    return wholeHours + 0.5;
  } else {
    return wholeHours + 1;
  }
};

/**
 * Calculate worked hours from timeIn/timeOut (mirrors backend bump.utils.ts)
 */
const calculateWorkedHours = (timeIn: DateTime, timeOut: DateTime): number => {
  const { hours: differenceInHours } = timeOut.diff(timeIn, 'hours');
  if (differenceInHours <= 0) return BUMP_MINIMAL_WORKED_HOURS;
  const roundedHours = roundHours(differenceInHours);
  return roundedHours || BUMP_MINIMAL_WORKED_HOURS;
};

const canEditHOServiceRepair = (
  repairType: string | undefined,
  canManageHOService: boolean,
): boolean => {
  if (repairType === RepairTypeEnum.HOService) return canManageHOService;
  return true;
};

/**
 * Whether a bump resource sheet row originates from a job phase bump
 * (vs a repair payment).
 */
const isJobPhaseBumpType = (bumpResourceType: string | undefined): boolean =>
  bumpResourceType === BumpResourceSheetType.JOB_BUMP_PHASE;

/**
 * Bump is waiting on a supervisor to approve or review logged hours.
 */
const isPendingSupervisorStatus = (
  status: JobPhaseBumpApprovalStatus | undefined,
): boolean =>
  status === JobPhaseBumpApprovalStatus.PENDING_APPROVAL ||
  status === JobPhaseBumpApprovalStatus.PENDING_REVIEW;

/**
 * Confirm-only repairs are logged without time/hours (ConfirmLogModal),
 * so hours validation doesn't apply to them.
 */
const isConfirmOnlyRepair = (
  repairType: string | undefined,
  estimatedHours: number | null | undefined,
): boolean => {
  if (repairType === RepairTypeEnum.Piecework) return true;
  return repairType === RepairTypeEnum.SpecialJobs && !estimatedHours;
};

export {
  getContractorFilter,
  findRepairFormula,
  calculateWorkedHours,
  canEditHOServiceRepair,
  isJobPhaseBumpType,
  isPendingSupervisorStatus,
  isConfirmOnlyRepair,
};
